import * as React from 'react';

import { accentAt } from '../core/palette';
import type { ChartSeries, ChartValueFormatter } from '../core/types';
import { useChartSize } from '../core/use-chart-size';
import { useChartTooltip } from '../core/use-chart-tooltip';
import { Legend } from '../primitives/Legend';
import { PieSlices } from '../primitives/PieSlices';
import { CHART_TOOLTIP_CLASS, ChartTooltipBody } from '../primitives/Tooltip';

import { cn } from '../../utils';

export interface PieChartProps {
  /** A pie renders exactly one series — its points become the slices */
  series: ChartSeries;
  height?: number;
  formatValue: ChartValueFormatter;
  /** Donut hole as a fraction of the radius */
  innerRatio?: number;
  showLegend?: boolean;
  ariaLabel?: string;
  className?: string;
}

/** Donut chart of a single series, one slice per point. */
export const PieChart = ({
  series,
  height = 240,
  formatValue,
  innerRatio = 0.45,
  showLegend = true,
  ariaLabel,
  className
}: PieChartProps) => {
  const { ref, width } = useChartSize();
  const { tooltip, show, hide } = useChartTooltip();
  const data = React.useMemo(
    () => series.points.map(point => ({ label: String(point.x), value: point.y })),
    [series]
  );

  const radius = Math.max(Math.min(width, height) / 2 - 8, 0);
  const center = { x: width / 2, y: height / 2 };

  const handleHover = (index: number | null, position: { x: number; y: number }): void => {
    if (index === null) {
      hide();
      return;
    }

    const datum = data[index];
    show({
      x: center.x + position.x,
      y: center.y + position.y,
      title: datum.label,
      entries: [
        {
          label: series.label,
          value: formatValue(datum.value, series.currency),
          color: accentAt(index)
        }
      ]
    });
  };

  return (
    <div className={className}>
      <div ref={ref} className='relative w-full' style={{ height }}>
        <svg
          width='100%'
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role='img'
          aria-label={ariaLabel}
          data-slot='chart-root'
        >
          <g transform={`translate(${center.x},${center.y})`}>
            <PieSlices data={data} radius={radius} innerRatio={innerRatio} onHover={handleHover} />
          </g>
        </svg>
        {tooltip && (
          <div
            data-slot='chart-tooltip'
            role='tooltip'
            className={cn(CHART_TOOLTIP_CLASS, '-translate-y-1/2')}
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            <ChartTooltipBody title={tooltip.title} entries={tooltip.entries} />
          </div>
        )}
      </div>
      {showLegend && (
        <Legend
          items={data.map((datum, index) => ({ label: datum.label, color: accentAt(index) }))}
        />
      )}
    </div>
  );
};

PieChart.displayName = 'PieChart';
